import React from 'react'
import styled, { css } from 'styled-components'
import {
    containerWidth,
    containerPadding,
    api,
    sdk,
    optional,
    trustandsafety,
    resource,
    tosLinks,
    containerMobilePadding,
} from '../../constants'
import { TitleLink } from '../../../types/title-link'
import { mobile } from '../../utils/media-query'
import {
    giphyIndigo,
    giphyPurple,
    giphyLightBlue,
    giphyBlue,
    giphyGreen,
    giphyAqua,
    giphyWhite,
    giphyLightestGrey,
} from '../../../styles/colors'
import ContextLink from '../../context-link'

const Dropdown = styled.div<DropdownMenuProps>`
    display: flex;
    justify-content: center;
    height: 434px;
    background: linear-gradient(-135deg, ${giphyGreen} 0%, ${giphyPurple} 100%);

    svg {
        width: auto;
        mix-blend-mode: normal;
        opacity: 0.8;
    }

    ${({ isFooter }) =>
        isFooter &&
        css`
            width: 100%;
            background: transparent;
        `}

    ${mobile.css`
        height: 100%;
        padding: 0 ${containerMobilePadding}px;
        box-sizing: border-box;
    `}
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: ${containerWidth}px;
    height: 100%;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    padding: 70px ${containerPadding}px;

    ${mobile.css`
        width: 100%;
        padding: 50px 0;
    `}
`

const MenuColumns = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 20px;
    line-height: 28px;

    ${mobile.css`
        flex-wrap: wrap;
        margin-bottom: 4px;
    `}
`

const MenuColumn = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;

    ${mobile.css`
        height: auto;
        flex-basis: 45%;
    `}
`

const MenuColumnHeader = styled.h5`
    margin-bottom: 0;
`

const MenuColumnLinks = styled.div<DropdownMenuProps>`
    display: flex;
    flex-direction: column;
    flex: 1;
    margin: 30px 0;
    line-height: 30px;
    font-family: 'PT Mono', monospace;

    ${mobile.css`
        margin: 26px 0 30px 0;
        line-height: 30px;
    `}

    ${({ isFooter }) =>
        isFooter &&
        `
        -webkit-text-fill-color: white;
        font-weight: normal;
        `}
`

type MenuColumnAProps = {
    isFooter?: boolean
}
const MenuColumnA = styled(ContextLink)<MenuColumnAProps>`
    color: ${({ isFooter }) => (isFooter ? `${giphyLightestGrey}` : 'white')};
    text-decoration: none;
    border-bottom: none;
    font-size: 18px;
    padding-bottom: 4px;

    &:hover {
        border-bottom: 1px solid ${({ isFooter }) => (isFooter ? `${giphyLightestGrey}` : 'white')};
    }
`

const ToS = styled.div`
    display: grid;
    max-width: 800px;
    width: 100%;
    grid-template-columns: repeat(5, auto);
    align-self: flex-start;

    ${mobile.css`
        display: flex;
        flex-wrap: wrap;
        width: 100%
    `}
`

const ToSLink = styled.a`
    color: white;
    font-size: 16px;
    text-decoration: none;
    border-bottom: none;
    margin-right: 20px;
    opacity: 0.6;

    &:last-child {
        margin-right: 0;
    }

    ${mobile.css`
        flex-basis: 50%;
        margin: 5px 0;
    `}
`

type ColumnProps = {
    [header: string]: TitleLink[]
}

type DropdownMenuProps = {
    isFooter?: boolean
}
const DropdownMenu = ({ isFooter }: DropdownMenuProps) => {
    const columns: ColumnProps = {
        SDKs: sdk,
        API: api,
        'Optional Settings': optional,
        'Trust And Safety': trustandsafety,
        Resources: resource,
    }

    const headerColors = [giphyIndigo, giphyLightBlue, giphyBlue, giphyAqua]

    const colKeys = Object.keys(columns)

    return (
        <Dropdown isFooter={isFooter}>
            <Content>
                <MenuColumns>
                    {colKeys.map((key, index) => (
                        <MenuColumn key={index}>
                            <MenuColumnHeader style={{ color: isFooter ? headerColors[index] : giphyWhite }}>
                                {key}
                            </MenuColumnHeader>
                            <MenuColumnLinks isFooter={isFooter}>
                                {columns[key].map((item, index) => (
                                    <span key={index}>
                                        <MenuColumnA to={item.link} isFooter={isFooter}>
                                            {item.title}
                                        </MenuColumnA>
                                    </span>
                                ))}
                            </MenuColumnLinks>
                        </MenuColumn>
                    ))}
                </MenuColumns>
                <ToS>
                    {tosLinks.map((item, index) => (
                        <ToSLink href={item.link} key={index}>
                            {item.title}
                        </ToSLink>
                    ))}
                </ToS>
            </Content>
        </Dropdown>
    )
}

export default DropdownMenu
